/**
 * TanStack Query hook for the admin users list (STORY-008-007 UI / 008-011 API).
 * `useUsers(q)` — `useInfiniteQuery` over the cursor-paginated, server-searched
 * `GET /admin/users`. The query key includes `q` so a search re-fetches.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  assignUserRoles,
  listPermissions,
  listRoles,
  listUsers,
  userKeys,
  type AdminRole,
  type AdminUserListResponse,
  type PermissionCatalogue,
  type RoleAssignResponse,
} from "@/features/admin/users/api";

const PAGE_LIMIT = 25;

export function useUsers(
  q: string,
): UseInfiniteQueryResult<InfiniteData<AdminUserListResponse>, Error> {
  const normalized = q.trim();
  return useInfiniteQuery<
    AdminUserListResponse,
    Error,
    InfiniteData<AdminUserListResponse>,
    ReturnType<typeof userKeys.list>,
    string | null
  >({
    queryKey: userKeys.list({ q: normalized }),
    queryFn: ({ pageParam }) =>
      listUsers({
        q: normalized === "" ? null : normalized,
        cursor: pageParam,
        limit: PAGE_LIMIT,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

export function useRoles(): UseQueryResult<{ items: AdminRole[] }, Error> {
  return useQuery<{ items: AdminRole[] }, Error>({
    queryKey: userKeys.roles(),
    queryFn: listRoles,
    staleTime: 60_000,
  });
}

export function usePermissionCatalogue(): UseQueryResult<
  PermissionCatalogue,
  Error
> {
  return useQuery<PermissionCatalogue, Error>({
    queryKey: userKeys.permissions(),
    queryFn: listPermissions,
    staleTime: 60_000,
  });
}

export function useAssignUserRoles(): UseMutationResult<
  RoleAssignResponse,
  Error,
  { userId: string; roleSlugs: string[] }
> {
  const qc = useQueryClient();
  return useMutation<
    RoleAssignResponse,
    Error,
    { userId: string; roleSlugs: string[] }
  >({
    mutationFn: ({ userId, roleSlugs }) => assignUserRoles(userId, roleSlugs),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}
